import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Typography, Avatar, Modal, Image, Flex, Checkbox } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  addPreviousMessages,
  exitSelectionMode,
  setMessagesList,
  toggleMessageSelection,
  updateConversationList,
} from "../../../redux/slices/chatSlice";
import { useLazyGetChatMessagesQuery } from "../../../redux/services/chatsApi";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { formatTextMessage } from "../../../utils";
import { useSocket } from "../../../context/SocketContext";
import { useChatSidebarContext } from "../../../context/ChatSidebarScreenContext";
import { toast, ToastContainer } from "react-toastify";
import ChatHeader from "../../../components/Chat/ChatHeader";
import GroupCallInvite from "../../../components/Chat/GroupCallInvite";
import ChatFooter from "../../../components/Chat/ChatFooter";
import CallVoiceBubble from "../../../components/Chat/CallVoiceBubble";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import CustomLoader from "../../../components/common/UI/Loader";
import ChatBubble from "../../../components/Chat/ChatBubble";
import messageCacheDB from "../../../services/messageCacheDB";
import { messageGraph } from "../../../services/messageGraph";
import { perfLogger } from "../../../services/perfLogger";
import { FEATURE_FLAGS } from "../../../constants";
import { useGraphMessages } from "../../../hooks/useGraphMessages";
import { storageService } from "../../../services/storageService";
import MediaViewer, { MediaItem } from "../../../components/Chat/MediaViewer";
import { computeDivider } from "../../../utils/computeDivider";
import type { ChatMessage } from "../../../utils/computeDivider";
import { useUnreadDivider } from "./hooks/useUnreadDivider";
import { useScrollEngine } from "./hooks/useScrollEngine";
import { useSocketMessages } from "./hooks/useSocketMessages";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "./index.scss";

const { Text } = Typography;

type ReplyMessage = {
  id: string | number;
  message: string;
  filePath?: string;
  mediaType?: number;
} | null;

const PAGE_LIMIT = 30;

const ChatRightSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const { socket, isConnected } = useSocket();
  const { setCurrentScreen } = useChatSidebarContext();

  if (FEATURE_FLAGS.USE_CRDT_ENGINE) perfLogger.enable();

  const {
    currentChat,
    messagesList,
    isSelectionMode,
    msgsToForward,
    initialUnreadCount,
    lastReadTimestamps,
  } = useSelector((state: RootState) => state.chat);
  const { contacts } = useSelector((state: RootState) => state.contacts);

  const userId = storageService.getUserId();

  // ── Shared refs ──────────────────────────────────────────────────────────
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const messagesListRef = useRef<ChatMessage[]>(messagesList as ChatMessage[]);
  const selectedChatRef = useRef(currentChat);
  const messageRefs = useRef<Map<string, HTMLElement>>(new Map());
  const lastReadTimestampsRef = useRef(lastReadTimestamps);
  const userIdRef = useRef(userId);
  const forwardedMsgIdsRef = useRef<Set<string>>(new Set());
  const expectedChatIdRef = useRef<string | null>(null);
  const lastViewportReadRef = useRef<string | null>(null);
  const unreadDividerRef = useRef<HTMLDivElement | null>(null);

  // Owned at component level so both getChat and useScrollEngine can share them.
  const scrollAnchorRef = useRef<"bottom" | string | null>(null);
  const pendingScrollRef = useRef<"bottom" | string | null>(null);

  // ── Component state ───────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [replyingTo, setReplyingTo] = useState<ReplyMessage>(null);
  const [hasMore, setHasMore] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteMsgLoading, setDeleteMsgLoading] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null,
  );
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);

  const [triggerGetChatMessages, { isFetching: loading }] =
    useLazyGetChatMessagesQuery();

  // ── Unread / divider hook ─────────────────────────────────────────────────
  const unread = useUnreadDivider(dispatch);

  // ── API: fetch messages ───────────────────────────────────────────────────
  // Defined after useUnreadDivider (needs unread.lastReadAtRef, unread.setFirstUnreadMsgId,
  // unread.setDividerUnreadCount) and before useScrollEngine (passed as a stable dep).
  // Uses scrollAnchorRef and pendingScrollRef from component scope.
  const getChat = useCallback(
    async (id: string | undefined, newPage: number) => {
      if (!userId || !id) return;
      const stopTimer = perfLogger.time("chat_load_api", id);
      try {
        const res = await triggerGetChatMessages({
          chatId: id,
          page: newPage,
          limit: PAGE_LIMIT,
          userId,
        }).unwrap();

        stopTimer();
        if (expectedChatIdRef.current !== id) return;

        const formattedMessages: ChatMessage[] = res.messages;
        if (formattedMessages.length === 0) {
          setHasMore(false);
          return;
        }

        if (newPage === 1) {
          const tempMsgs = messagesListRef.current.filter((m) => m.isTempMsg);
          const cmd = pendingScrollRef.current;
          pendingScrollRef.current = null;

          if (cmd === "bottom") {
            scrollAnchorRef.current = "bottom";
          } else if (typeof cmd === "string" && cmd.startsWith("__unread__")) {
            const count = parseInt(cmd.slice("__unread__".length), 10);
            const { firstUnreadId, count: actualCount } = computeDivider(
              formattedMessages,
              unread.lastReadAtRef.current,
              count,
            );
            scrollAnchorRef.current = firstUnreadId ?? "bottom";
            unread.setFirstUnreadMsgId(firstUnreadId);
            if (firstUnreadId) unread.setDividerUnreadCount(actualCount);
          }

          if (FEATURE_FLAGS.USE_CRDT_ENGINE) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formattedMessages.forEach((m: any) => messageGraph.mirror(id, m));
          }
          dispatch(setMessagesList([...formattedMessages, ...tempMsgs]));
        } else {
          if (FEATURE_FLAGS.USE_CRDT_ENGINE) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formattedMessages.forEach((m: any) => messageGraph.mirror(id, m));
          }
          const existingIds = new Set(
            messagesListRef.current.map((m) => String(m.id)),
          );
          const fresh = formattedMessages.filter(
            (m) => !existingIds.has(String(m.id)),
          );
          if (fresh.length > 0) dispatch(addPreviousMessages(fresh));
        }

        setHasMore(
          formattedMessages.length >= PAGE_LIMIT && res.hasNextPage !== false,
        );

        messageCacheDB
          .putMessages(userId, id, formattedMessages)
          .catch(() => {});
        const newest = formattedMessages[formattedMessages.length - 1];
        messageCacheDB
          .putMeta(userId, id, {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            lastTimestamp:
              (newest as any)?.createdAt || new Date().toISOString(),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            lastMessageId: (newest as any)?.id || "",
            hasMore:
              formattedMessages.length >= PAGE_LIMIT &&
              res.hasNextPage !== false,
            cachedAt: Date.now(),
          })
          .catch(() => {});

        return res;
      } catch (err: unknown) {
        stopTimer();
        const e = err as { name?: string; message?: string };
        if (
          e?.name === "AbortError" ||
          e?.message === "The user aborted a request."
        )
          return;
      }
    },
    [
      dispatch,
      triggerGetChatMessages,
      userId,
      unread.lastReadAtRef,
      unread.setFirstUnreadMsgId,
      unread.setDividerUnreadCount,
    ], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Scroll engine hook ────────────────────────────────────────────────────
  const scroll = useScrollEngine({
    messagesContainerRef,
    messagesListRef,
    selectedChatRef,
    messageRefs,
    scrollAnchorRef,
    messagesList: messagesList as ChatMessage[],
    loading,
    hasMore,
    page,
    setPage,
    currentChatId: currentChat?.id,
    userId,
    getChat,
    dispatch,
    unreadBadgeCountRef: unread.unreadBadgeCountRef,
    dividerClearTimerRef: unread.dividerClearTimerRef,
    persistRead: unread.persistRead,
    persistReadRef: unread.persistReadRef,
    setUnreadBadgeCount: unread.setUnreadBadgeCount,
    setFirstUnreadMsgId: unread.setFirstUnreadMsgId,
    setDividerUnreadCount: unread.setDividerUnreadCount,
    clearDivider: unread.clearDivider,
  });

  // ── Socket messages hook ──────────────────────────────────────────────────
  useSocketMessages({
    socket,
    isConnected,
    dispatch,
    selectedChatRef,
    messagesListRef,
    messagesContainerRef,
    userIdRef,
    pendingScrollRef,
    getChat,
    isAtBottomRef: scroll.isAtBottomRef,
    autoScrollRef: scroll.autoScrollRef,
    shouldPinRef: scroll.shouldPinRef,
    userScrolledUpRef: scroll.userScrolledUpRef,
    unreadBadgeCountRef: unread.unreadBadgeCountRef,
    persistReadRef: unread.persistReadRef,
    setUnreadBadgeCount: unread.setUnreadBadgeCount,
    setDividerUnreadCount: unread.setDividerUnreadCount,
    setFirstUnreadMsgId: unread.setFirstUnreadMsgId,
  });

  // ── Ref mirrors ───────────────────────────────────────────────────────────
  useEffect(() => {
    messagesListRef.current = messagesList as ChatMessage[];
  }, [messagesList]);
  useEffect(() => {
    lastReadTimestampsRef.current = lastReadTimestamps;
  }, [lastReadTimestamps]);
  useEffect(() => {
    selectedChatRef.current = currentChat;
  }, [currentChat]);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    try {
      const ids: string[] = storageService.getForwardedMsgIds();
      forwardedMsgIdsRef.current = new Set(ids);
    } catch {
      forwardedMsgIdsRef.current = new Set();
    }
  }, [messagesList]);

  // ── Derived memos ─────────────────────────────────────────────────────────
  const mediaItems = React.useMemo<MediaItem[]>(() => {
    return (
      (messagesList || [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter(
          (msg: any) => msg.filePath && [1, 2, 3, 4].includes(msg.mediaType),
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((msg: any) => ({
          id: msg.id,
          mediaType: msg.mediaType as 1 | 2 | 3 | 4,
          filePath: msg.filePath,
          fileName: msg.fileName,
          senderName:
            msg.senderName ||
            (msg.isUser ? storageService.getFullName() || "Me" : msg.sender),
          timestamp: msg.timestamp,
        }))
    );
  }, [messagesList]);

  const messageMap = React.useMemo(() => {
    const map = new Map<string, ChatMessage>();
    (messagesList as ChatMessage[]).forEach((msg) => {
      if (msg.id) map.set(String(msg.id), msg);
    });
    return map;
  }, [messagesList]);

  // ── Callbacks ─────────────────────────────────────────────────────────────
  const openMediaViewer = useCallback(
    (msgId: string | number) => {
      const idx = mediaItems.findIndex(
        (item) => String(item.id) === String(msgId),
      );
      if (idx !== -1) {
        setMediaViewerIndex(idx);
        setMediaViewerOpen(true);
      }
    },
    [mediaItems],
  );

  const setMessageRef = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) messageRefs.current.set(id, el);
      else messageRefs.current.delete(id);
    },
    [],
  );

  const scrollToMessage = useCallback(
    async (messageId: string) => {
      const scrollAndHighlight = (el: HTMLElement) => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("highlight-message");
        setTimeout(() => el.classList.remove("highlight-message"), 2000);
      };

      const existingEl = messageRefs.current.get(messageId);
      if (existingEl) {
        scrollAndHighlight(existingEl);
        return;
      }

      if (messageMap.has(messageId)) {
        setTimeout(() => {
          const el = messageRefs.current.get(messageId);
          if (el) scrollAndHighlight(el);
        }, 100);
        return;
      }

      if (!currentChat?.id) return;

      if (userId) {
        try {
          const cached = await messageCacheDB.getMessage(
            userId,
            currentChat.id,
            messageId,
          );
          if (cached) {
            perfLogger.log({
              event: "idb_pagination_hit",
              chatId: currentChat.id,
              durationMs: 0,
            });
            const window = await messageCacheDB.getMessagesAroundTimestamp(
              userId,
              currentChat.id,
              cached.createdAt,
              40,
            );
            if (window.length > 0) {
              if (FEATURE_FLAGS.USE_CRDT_ENGINE) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                window.forEach((m: any) =>
                  messageGraph.mirror(currentChat.id, m),
                );
              }
              dispatch(setMessagesList(window));
              scrollAnchorRef.current = messageId;
              setTimeout(() => {
                const el = messageRefs.current.get(messageId);
                if (el) scrollAndHighlight(el);
              }, 100);
              return;
            }
          }
        } catch {
          // IDB miss — fall through to API
        }
      }

      if (!hasMore) return;

      perfLogger.log({
        event: "idb_pagination_miss",
        chatId: currentChat.id,
        durationMs: 0,
      });
      toast.info(
        <Flex align="center" gap={10}>
          <CustomLoader height={15} width={2} />
          <Text style={{ fontSize: "13px", color: "inherit" }}>
            Searching for message...
          </Text>
        </Flex>,
        {
          autoClose: false,
          toastId: "search-msg",
          className: "custom-toast-sm",
          containerId: "chat-toast-container",
          closeButton: false,
          icon: false,
        },
      );

      try {
        let nextPage = page;
        let isFound = false;

        while (!isFound && hasMore) {
          nextPage += 1;
          const res = await getChat(currentChat.id, nextPage);
          if (!res) break;
          setPage(nextPage);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((res as any).messages.some((m: any) => m.id === messageId)) {
            isFound = true;
            setTimeout(() => {
              const el = messageRefs.current.get(messageId);
              if (el) scrollAndHighlight(el);
            }, 300);
            break;
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (!(res as any).hasNextPage) break;
        }

        if (!isFound) toast.error("Message not found in history");
      } catch (error) {
        console.error("Error while searching:", error);
      } finally {
        toast.dismiss("search-msg");
      }
    },
    [currentChat?.id, getChat, hasMore, messageMap, page, userId, dispatch],
  );

  const deleteMessage = async () => {
    if (!selectedMessageId) return;
    setDeleteMsgLoading(true);
    try {
      await apiInstance.delete(ENDPOINTS.DELETE_MESSAGE, {
        data: { messageIds: [selectedMessageId] },
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updatedMessages = (messagesList as any[]).filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (msg: any) => msg.id !== selectedMessageId,
      );
      dispatch(setMessagesList(updatedMessages));

      if (FEATURE_FLAGS.USE_MESSAGE_GRAPH && selectedMessageId) {
        messageGraph.remove(selectedMessageId);
      }
      if (userId && selectedMessageId) {
        messageCacheDB.removeMessage(userId, selectedMessageId).catch(() => {});
      }

      const lastMsgObj = updatedMessages[updatedMessages.length - 1];
      dispatch(
        updateConversationList({
          id: currentChat?.id,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          message: lastMsgObj ? (lastMsgObj as any).message : "",
        }),
      );

      setDeleteModalVisible(false);
      setSelectedMessageId(null);
    } catch (err) {
      console.error("Error deleting conversation:", err);
    } finally {
      setDeleteMsgLoading(false);
    }
  };

  const showchatDeleteModal = (id: string) => {
    setDeleteModalVisible(true);
    setSelectedMessageId(id);
  };

  // ── Chat initialisation ───────────────────────────────────────────────────
  useEffect(() => {
    if (!currentChat?.id || !userId) return;
    const chatId = currentChat.id;
    const unreadCount = initialUnreadCount;

    const lastReadAt = lastReadTimestampsRef.current[chatId] ?? null;
    unread.lastReadAtRef.current = lastReadAt;

    expectedChatIdRef.current = chatId;
    lastViewportReadRef.current = null;
    setReplyingTo(null);
    dispatch(exitSelectionMode());
    setPage(1);

    const startAtBottom = unreadCount === 0;
    scroll.setIsAtBottom(startAtBottom);
    unread.setUnreadBadgeCount(startAtBottom ? 0 : unreadCount);
    unread.unreadBadgeCountRef.current = startAtBottom ? 0 : unreadCount;
    unread.setFirstUnreadMsgId(null);
    unread.setDividerUnreadCount(0);
    if (unread.dividerClearTimerRef.current) {
      clearTimeout(unread.dividerClearTimerRef.current);
      unread.dividerClearTimerRef.current = null;
    }

    scroll.shouldPinRef.current = startAtBottom;
    scroll.userScrolledUpRef.current = !startAtBottom;
    scroll.isAtBottomRef.current = startAtBottom;

    dispatch(setMessagesList([]));
    scrollAnchorRef.current = null;

    let cancelled = false;

    (async () => {
      try {
        const cached = await messageCacheDB.getMessages(userId, chatId);
        if (cancelled || expectedChatIdRef.current !== chatId) return;

        if (cached.length > 0) {
          const sliced = cached.slice(-50) as ChatMessage[];
          if (unreadCount === 0) {
            scrollAnchorRef.current = "bottom";
          } else {
            const { firstUnreadId, count } = computeDivider(
              sliced,
              lastReadAt,
              unreadCount,
            );
            if (firstUnreadId) {
              // IDB has the first unread — jump straight to it.
              scrollAnchorRef.current = firstUnreadId;
              unread.setFirstUnreadMsgId(firstUnreadId);
              unread.setDividerUnreadCount(count);
            }
            // If firstUnreadId is null the unread messages aren't in IDB yet.
            // Leave scrollAnchorRef null — the API response (getChat → __unread__)
            // will set the correct anchor. Never fall back to "bottom" here:
            // that would fire the rAF scroll handler which calls persistRead and
            // resets unreadBadgeCount to 0 before the API has even loaded.
          }
          if (FEATURE_FLAGS.USE_CRDT_ENGINE) {
            messageGraph.rebuildFromMessages(chatId, cached);
          }
          dispatch(setMessagesList(sliced));

          const meta = await messageCacheDB.getMeta(userId, chatId);
          if (!cancelled && meta) setHasMore(meta.hasMore);
        }
      } catch {
        // IDB unavailable — API will hydrate messages
      }

      if (cancelled) return;

      pendingScrollRef.current =
        unreadCount > 0 ? `__unread__${unreadCount}` : "bottom";
      getChat(chatId, 1);
    })();

    return () => {
      cancelled = true;
      pendingScrollRef.current = null;
    };
  }, [getChat, currentChat?.id, dispatch, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scheduled message refresh ─────────────────────────────────────────────
  useEffect(() => {
    if (!messagesList.length || !currentChat?.id) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const upcomingMsg = (messagesList as any[])
      .filter(
        (msg) => msg.scheduleTime && moment(msg.scheduleTime).isAfter(moment()),
      )
      .sort((a, b) => moment(a.scheduleTime).diff(moment(b.scheduleTime)))[0];
    if (!upcomingMsg) return;
    const delay = moment(upcomingMsg.scheduleTime).diff(moment());
    if (delay <= 0) return;
    const timeout = setTimeout(() => {
      getChat(currentChat.id, 1);
    }, delay);
    return () => clearTimeout(timeout);
  }, [messagesList, currentChat?.id, getChat]);

  // ── IDB cleanup ───────────────────────────────────────────────────────────
  useEffect(() => {
    messageCacheDB.cleanup().catch(() => {});
  }, []);

  // ── Dev debug handle ──────────────────────────────────────────────────────
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    (
      window as Window & typeof globalThis & { __chatDebug?: unknown }
    ).__chatDebug = {
      graph: (chatId?: string) =>
        messageGraph.snapshot(chatId ?? currentChat?.id ?? ""),
      idb: async (chatId?: string) => {
        const cid = chatId ?? currentChat?.id;
        if (!cid || !userId) return null;
        const msgs = await messageCacheDB.getMessages(userId, cid);
        return {
          count: msgs.length,
          oldest: msgs[0]?.createdAt ?? null,
          newest: msgs[msgs.length - 1]?.createdAt ?? null,
        };
      },
      state: () => ({
        chatId: currentChat?.id,
        page,
        hasMore,
        reduxCount: messagesList.length,
        graphCount: messageGraph.size(currentChat?.id ?? ""),
        shouldPin: scroll.shouldPinRef.current,
        isAtBottom: scroll.isAtBottomRef.current,
        userScrolledUp: scroll.userScrolledUpRef.current,
      }),
    };
  }, [currentChat?.id, page, hasMore, messagesList.length, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Date label helper ─────────────────────────────────────────────────────
  const formatMessageDate = (dateString: string) => {
    const date = moment(dateString);
    const today = moment().startOf("day");
    const yesterday = moment().subtract(1, "day").startOf("day");
    if (date.isSame(today, "day")) return "Today";
    if (date.isSame(yesterday, "day")) return "Yesterday";
    if (date.isAfter(moment().subtract(7, "days"), "day"))
      return date.format("dddd");
    return date.format("MMMM D, YYYY");
  };

  // ── Graph-reconciled messages ─────────────────────────────────────────────
  const messages = useGraphMessages(currentChat?.id, messagesList);
  const { firstUnreadMsgId, setFirstUnreadMsgId, persistRead } = unread;

  // ── Unread count — pure derivation from Redux SSoT ────────────────────────
  // Recomputes whenever messagesList or lastReadTimestamps changes.
  // No local counter state; no side-effects that fight the scroll engine.
  const activeUnreadCount = useMemo(() => {
    if (!currentChat?.id) return 0;
    const lastReadAt = lastReadTimestamps[currentChat.id] ?? null;
    if (!lastReadAt) return 0;
    const lastReadMs = new Date(lastReadAt).getTime();
    return (messages as ChatMessage[]).filter((msg) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((msg as any).isUser || (msg as any).isTempMsg || !msg.createdAt)
        return false;
      return new Date(msg.createdAt).getTime() > lastReadMs;
    }).length;
  }, [messages, lastReadTimestamps, currentChat?.id]);

  // ── Dismiss divider and mark as read when user reaches the bottom ─────────

  useEffect(() => {
    if (!scroll.isAtBottom || !currentChat?.id) return;

    const latestMsg = [...(messages as ChatMessage[])]
      .reverse()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .find((m) => m.createdAt && !(m as any).isTempMsg);
    if (latestMsg?.createdAt) {
      persistRead(currentChat.id, latestMsg.createdAt, 0);
    }
    setFirstUnreadMsgId(null);
  }, [
    scroll.isAtBottom,
    currentChat?.id,
    messages,
    persistRead,
    setFirstUnreadMsgId,
  ]);

  // ── Dismiss divider when user scrolls DOWN past it ────────────────────────

  useEffect(() => {
    const dividerEl = unreadDividerRef.current;
    const container = messagesContainerRef.current;
    if (!dividerEl || !container || !firstUnreadMsgId || !currentChat?.id)
      return;

    const chatId = currentChat.id;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) return;
        // Exited from the top — user scrolled DOWN past the divider.
        const rootTop = entry.rootBounds?.top ?? 0;
        if (entry.boundingClientRect.bottom >= rootTop) return;

        const latest = [...messagesListRef.current]
          .reverse()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .find((m) => m.createdAt && !(m as any).isTempMsg);
        persistRead(chatId, latest?.createdAt ?? new Date().toISOString(), 0);
        setFirstUnreadMsgId(null);
      },
      { root: container, threshold: 0 },
    );

    observer.observe(dividerEl);
    return () => observer.disconnect();
  }, [firstUnreadMsgId, currentChat?.id, persistRead, setFirstUnreadMsgId]);

  return (
    <>
      <div className="chat-right-section">
        <ToastContainer
          containerId="chat-toast-container"
          position="top-center"
          className="chat-toast-container"
        />
        <ChatHeader />
        <GroupCallInvite />

        <div ref={messagesContainerRef} className="chat-area">
          {loading && (
            <div className="loader-wraper">
              <CustomLoader />
            </div>
          )}

          {(messages || []).map((rawMsg: ChatMessage, index: number) => {
            const msg = forwardedMsgIdsRef.current.has(String(rawMsg.id))
              ? { ...rawMsg, isForwarded: true }
              : rawMsg;
            const prevMsg = messages[index - 1] as ChatMessage | undefined;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const isCreateGCOrAddMemberNotification =
              (msg as any).mediaType === -1;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const isCallNotification = (msg as any).mediaType === 6;
            const isNewDay =
              !prevMsg ||
              formatMessageDate(prevMsg.createdAt ?? "") !==
                formatMessageDate(msg.createdAt ?? "");
            const isFirstUnread =
              !!unread.firstUnreadMsgId &&
              String(msg.id) === String(unread.firstUnreadMsgId);

            return (
              <React.Fragment key={String(msg.id)}>
                {isNewDay && (
                  <Text className="day-badge">
                    {formatMessageDate(msg.createdAt)}
                  </Text>
                )}
                {isFirstUnread && (
                  <div
                    className="unread-messages-divider"
                    ref={unreadDividerRef}
                  >
                    <span>
                      {activeUnreadCount <= 1
                        ? "1 unread message"
                        : `${activeUnreadCount > 99 ? "99+" : activeUnreadCount} unread messages`}
                    </span>
                  </div>
                )}

                {isCreateGCOrAddMemberNotification ? (
                  <Flex justify="center" ref={setMessageRef(String(msg.id))}>
                    <Text className="group-notification">
                      {formatTextMessage(msg, contacts)}
                    </Text>
                  </Flex>
                ) : isCallNotification ? (
                  <div ref={setMessageRef(String(msg.id))}>
                    <CallVoiceBubble msg={msg} />
                  </div>
                ) : (
                  <Flex
                    align="center"
                    gap={8}
                    ref={setMessageRef(String(msg.id))}
                    className={`msg-row ${isSelectionMode ? "msg-row--selection" : ""}${
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      isSelectionMode &&
                      msgsToForward.some((m: any) => m.id === msg.id)
                        ? " msg-row--selected"
                        : ""
                    }`}
                    onClick={
                      isSelectionMode
                        ? () => dispatch(toggleMessageSelection(msg))
                        : undefined
                    }
                    style={isSelectionMode ? { cursor: "pointer" } : undefined}
                  >
                    {isSelectionMode && (
                      <Checkbox
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        checked={msgsToForward.some(
                          (m: any) => m.id === msg.id,
                        )}
                        className="msg-select-checkbox"
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => dispatch(toggleMessageSelection(msg))}
                      />
                    )}

                    {!(msg as { isUser?: boolean }).isUser && (
                      <Avatar
                        src={currentChat?.image}
                        className="other-sender-avatar"
                        size="large"
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        {(msg as any).sender ? (msg as any).sender[0] : "+"}
                      </Avatar>
                    )}

                    <Flex
                      flex={1}
                      justify={
                        (msg as { isUser?: boolean }).isUser ? "end" : "start"
                      }
                    >
                      <ChatBubble
                        msg={msg}
                        setReplyingTo={setReplyingTo}
                        showchatDeleteModal={showchatDeleteModal}
                        isUser={!!(msg as { isUser?: boolean }).isUser}
                        scrollToMessage={scrollToMessage}
                        onMediaClick={openMediaViewer}
                        isSelectionMode={isSelectionMode}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isSelected={msgsToForward.some(
                          (m: any) => m.id === msg.id,
                        )}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onToggleSelect={(m: any) =>
                          dispatch(toggleMessageSelection(m))
                        }
                      />
                    </Flex>
                  </Flex>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {!scroll.isAtBottom && (
          <div className="scroll-to-bottom-wrapper">
            {activeUnreadCount > 0 && (
              <span className="scroll-to-bottom-label" key={activeUnreadCount}>
                {activeUnreadCount}
              </span>
            )}

            <button
              className="scroll-to-bottom-btn"
              onClick={scroll.handleScrollToBottomClick}
            >
              <img
                src="/assets/icons/down-arrow.svg"
                alt="scroll to bottom"
                width={25}
                height={25}
                className="down-arrow-icon"
              />
            </button>
          </div>
        )}

        {replyingTo && (
          <div className="reply-box">
            {replyingTo?.mediaType === 1 && replyingTo?.filePath && (
              <Image src={replyingTo?.filePath} height={100} />
            )}
            {replyingTo?.mediaType === 0 && replyingTo?.message && (
              <p>{replyingTo?.message}</p>
            )}
            {replyingTo?.mediaType === 4 && replyingTo?.filePath && (
              <audio
                className="custom-audio"
                controls
                controlsList="nodownload noplaybackrate"
              >
                <source src={replyingTo?.filePath} type="audio/wav" />
              </audio>
            )}
            <CloseOutlined
              className="cross-icon"
              onClick={() => setReplyingTo(null)}
            />
          </div>
        )}

        {isSelectionMode ? (
          <div className="selection-toolbar">
            <button
              className="selection-toolbar-btn cancel"
              onClick={() => {
                dispatch(exitSelectionMode());
                setCurrentScreen("chats");
              }}
            >
              Cancel
            </button>
            <Text className="selection-toolbar-count">
              {msgsToForward.length} selected
            </Text>
            <button
              className="selection-toolbar-btn forward"
              disabled={msgsToForward.length === 0}
              onClick={() => setCurrentScreen("forward-msg")}
            >
              Forward
            </button>
          </div>
        ) : (
          <ChatFooter
            getChat={getChat}
            replyingTo={replyingTo}
            setReplyingTo={setReplyingTo}
            scrollToBottom={scroll.scrollToBottom}
          />
        )}
      </div>

      <Modal
        centered
        title="Confirm Message Deletion"
        open={deleteModalVisible}
        onOk={deleteMessage}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        okButtonProps={{
          className: "modal-ok-btn",
          danger: true,
          loading: deleteMsgLoading,
        }}
        cancelButtonProps={{ className: "modal-cancel-btn" }}
        cancelText="Cancel"
        className="custom-modal"
      >
        <p className="text-primary">
          Are you sure you want to delete this message?
        </p>
      </Modal>

      <MediaViewer
        open={mediaViewerOpen}
        items={mediaItems}
        currentIndex={mediaViewerIndex}
        onClose={() => setMediaViewerOpen(false)}
        onIndexChange={setMediaViewerIndex}
      />
    </>
  );
};

export default ChatRightSection;
